/**
 * Live data for the patient chart header (Phase 6, PAT-11): the patient record (status, hold,
 * discharge fields), the allergy summary from demographics, and the dashboard (SOC checklist).
 * Each source loads independently; a failure leaves that part of the header in a neutral state.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPatientById } from '../../../services/admin/patient.service';
import { fetchDemographics } from '../../../services/patientmr/demographics.service';
import { CertificationPeriodsService } from '../../../services/patientmr/certificationPeriods.service';
import { PATIENT_CHART_UPDATED_EVENT } from '../../../constants/navigationEvents';
import type { PatientData } from '../../../dtos/admin/patient.dto';
import type { PatientDashboardData } from '../../../dtos/patientmr/certificationPeriod.dto';
import type { Allergy } from '../../../dtos/patientmr/demographics.dto';
import type { AdvanceDirectiveStatus } from '../../../dtos/patientmr/socItems.dto';

/** Phase 19 F19: the advance directive on the demographics record (header OOH-DNR banner) */
export interface AdvanceDirectiveSummary {
  status: AdvanceDirectiveStatus;
  formDate: string | null;
}

export function summarizeAdvanceDirective(
  record: { adStatus?: AdvanceDirectiveStatus | null; adFormDate?: string | null } | null
): AdvanceDirectiveSummary | null {
  if (!record?.adStatus) return null;
  return { status: record.adStatus, formDate: record.adFormDate ?? null };
}

export type AllergySummary =
  | { state: 'loading' }
  | { state: 'unavailable' }
  | { state: 'none-known' }
  | { state: 'not-recorded' }
  | { state: 'list'; allergens: string[] };

export function summarizeAllergies(record: { allergies?: Allergy[]; allergiesNoneKnown?: boolean } | null): AllergySummary {
  if (!record) return { state: 'unavailable' };
  const allergens = (record.allergies || []).map((a) => a.allergen?.trim()).filter((a): a is string => !!a);
  if (allergens.length > 0) return { state: 'list', allergens };
  if (record.allergiesNoneKnown) return { state: 'none-known' };
  return { state: 'not-recorded' };
}

interface Options {
  canReadDemographics: boolean;
  canReadDashboard: boolean;
}

export function usePatientChartSummary(patientId: string | undefined, options: Options) {
  const { canReadDemographics, canReadDashboard } = options;
  const [patient, setPatient] = useState<PatientData | null>(null);
  const [patientLoading, setPatientLoading] = useState(false);
  const [allergies, setAllergies] = useState<AllergySummary>({ state: 'loading' });
  const [dashboard, setDashboard] = useState<PatientDashboardData | null>(null);
  /** Overview v3 (OV-13): the Overview reuses this dashboard instead of loading its own copy */
  const [dashboardLoading, setDashboardLoading] = useState(Boolean(patientId && canReadDashboard));
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const dashboardRequestRef = useRef(0);
  const [advanceDirective, setAdvanceDirective] = useState<AdvanceDirectiveSummary | null>(null);
  const requestRef = useRef(0);

  const loadDashboard = useCallback(async () => {
    if (!patientId || !canReadDashboard) return;
    const requestId = ++dashboardRequestRef.current;
    const isCurrent = () => dashboardRequestRef.current === requestId;
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const d = await CertificationPeriodsService.getDashboard(patientId);
      if (isCurrent()) setDashboard(d);
    } catch (err: unknown) {
      if (isCurrent()) {
        setDashboard(null);
        setDashboardError(err instanceof Error ? err.message : 'Failed to load dashboard');
      }
    } finally {
      if (isCurrent()) setDashboardLoading(false);
    }
  }, [patientId, canReadDashboard]);

  const load = useCallback(async () => {
    if (!patientId) return;
    const requestId = ++requestRef.current;
    const isCurrent = () => requestRef.current === requestId;
    setPatientLoading(true);

    const patientPromise = fetchPatientById(patientId)
      .then((p) => {
        if (isCurrent()) setPatient(p);
      })
      .catch(() => undefined)
      .finally(() => {
        if (isCurrent()) setPatientLoading(false);
      });

    const allergyPromise = canReadDemographics
      ? fetchDemographics(patientId)
          .then((demo) => {
            if (!isCurrent()) return;
            setAllergies(summarizeAllergies(demo));
            // Same record as the SOC items: no extra request for the OOH-DNR banner
            setAdvanceDirective(summarizeAdvanceDirective(demo as typeof demo & { adStatus?: AdvanceDirectiveStatus | null; adFormDate?: string | null }));
          })
          .catch(() => {
            if (isCurrent()) setAllergies({ state: 'unavailable' });
          })
      : Promise.resolve(setAllergies({ state: 'unavailable' }));

    await Promise.all([patientPromise, allergyPromise, loadDashboard()]);
  }, [patientId, canReadDemographics, loadDashboard]);

  useEffect(() => {
    setPatient(null);
    setAllergies({ state: 'loading' });
    setDashboard(null);
    setAdvanceDirective(null);
    void load();
  }, [load]);

  useEffect(() => {
    const onUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ patientId?: string }>).detail;
      if (!detail?.patientId || detail.patientId === patientId) void load();
    };
    window.addEventListener(PATIENT_CHART_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(PATIENT_CHART_UPDATED_EVENT, onUpdated);
  }, [load, patientId]);

  return {
    patient, setPatient, patientLoading, allergies, dashboard, advanceDirective, reload: load,
    dashboardLoading, dashboardError, reloadDashboard: loadDashboard,
  };
}

export default usePatientChartSummary;
